/**
 * Pure geometric predicates (SD-4.2). No dependencies, no DOM, no React.
 *
 * Rings are open (no repeated closing vertex) and may be either winding unless a function says
 * otherwise. Everything is exact-arithmetic-free float geometry: the containment tests treat
 * *touching* as benign (a panel flush against the setback envelope is legal), so crossings are
 * proper crossings — strict sign changes — and boundary contact never flips a verdict.
 */

import type { Pt } from './types';

/** Shoelace signed area: positive for counter-clockwise rings (y up). */
export function signedArea(ring: readonly Pt[]): number {
  let a = 0;
  for (let i = 0, n = ring.length; i < n; i += 1) {
    const p = ring[i];
    const q = ring[(i + 1) % n];
    a += p[0] * q[1] - q[0] * p[1];
  }
  return a / 2;
}

export function polygonArea(ring: readonly Pt[]): number {
  return Math.abs(signedArea(ring));
}

/** Returns the ring counter-clockwise, reversing a copy only when needed. */
export function toCCW(ring: readonly Pt[]): Pt[] {
  return signedArea(ring) < 0 ? [...ring].reverse() : [...ring];
}

/** Twice the signed area of triangle a,b,p — positive when p is left of a→b. */
function isLeft(a: Pt, b: Pt, p: Pt): number {
  return (b[0] - a[0]) * (p[1] - a[1]) - (p[0] - a[0]) * (b[1] - a[1]);
}

/**
 * Ray-cast point-in-polygon. Points exactly on the boundary are winding-dependent coin flips;
 * callers that care about boundary contact must not use this to decide it.
 */
export function pointInPolygon(pt: Pt, ring: readonly Pt[]): boolean {
  const [x, y] = pt;
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i, i += 1) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

/**
 * Winding number of `ring` around `pt` — the tool that distinguishes a genuine inset lobe from
 * an inverted one. `offsetPolygonPerEdge` keeps a lobe only when the raw offset ring winds
 * around it in the face's own orientation; a ring that has flipped inside-out (setbacks that
 * consume the whole face) winds the other way, which a bare union cannot see because the
 * boolean library normalises winding on input.
 */
export function windingNumber(pt: Pt, ring: readonly Pt[]): number {
  let wn = 0;
  for (let i = 0, n = ring.length; i < n; i += 1) {
    const a = ring[i];
    const b = ring[(i + 1) % n];
    if (a[1] <= pt[1]) {
      if (b[1] > pt[1] && isLeft(a, b, pt) > 0) wn += 1;
    } else if (b[1] <= pt[1] && isLeft(a, b, pt) < 0) wn -= 1;
  }
  return wn;
}

/**
 * A point strictly inside a simple polygon (comp.geom FAQ construction): take a convex vertex V
 * (lowest, then leftmost); either the centroid of triangle (prev, V, next) is interior, or some
 * other ring vertex lies inside that triangle and the midpoint of V and the deepest such vertex
 * is. The centroid of the whole ring is NOT this — it can fall outside a concave face.
 */
export function interiorPoint(ring: readonly Pt[]): Pt {
  const n = ring.length;
  let vi = 0;
  for (let i = 1; i < n; i += 1) {
    if (ring[i][1] < ring[vi][1] || (ring[i][1] === ring[vi][1] && ring[i][0] < ring[vi][0])) vi = i;
  }
  const A = ring[(vi - 1 + n) % n];
  const V = ring[vi];
  const B = ring[(vi + 1) % n];
  const tri = Math.sign(isLeft(A, B, V));
  if (tri === 0) return [(A[0] + V[0] + B[0]) / 3, (A[1] + V[1] + B[1]) / 3];

  const inTriangle = (q: Pt): boolean => {
    const s1 = isLeft(A, V, q) * tri;
    const s2 = isLeft(V, B, q) * tri;
    const s3 = isLeft(B, A, q) * tri;
    // The triangle's own winding is tri-signed on all three edges only for one labelling;
    // accept either consistent labelling by testing both signs together.
    return (s1 >= 0 && s2 >= 0 && s3 >= 0) || (s1 <= 0 && s2 <= 0 && s3 <= 0);
  };

  let best: Pt | null = null;
  let bestDepth = -1;
  for (let i = 0; i < n; i += 1) {
    if (i === vi || i === (vi - 1 + n) % n || i === (vi + 1) % n) continue;
    const q = ring[i];
    if (!inTriangle(q)) continue;
    const depth = Math.abs(isLeft(A, B, q));
    if (depth > bestDepth) {
      bestDepth = depth;
      best = q;
    }
  }
  if (!best) return [(A[0] + V[0] + B[0]) / 3, (A[1] + V[1] + B[1]) / 3];
  return [(V[0] + best[0]) / 2, (V[1] + best[1]) / 2];
}

/** Proper (strict) segment crossing. Touching endpoints and collinear overlap are NOT crossings. */
export function segmentsProperlyCross(a: Pt, b: Pt, c: Pt, d: Pt): boolean {
  const d1 = isLeft(c, d, a);
  const d2 = isLeft(c, d, b);
  const d3 = isLeft(a, b, c);
  const d4 = isLeft(a, b, d);
  return ((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) && ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0));
}

const rectCorners = (x: number, y: number, w: number, h: number): Pt[] => [
  [x, y],
  [x + w, y],
  [x + w, y + h],
  [x, y + h],
];

const strictlyInRect = (p: Pt, x: number, y: number, w: number, h: number): boolean =>
  p[0] > x && p[0] < x + w && p[1] > y && p[1] < y + h;

/**
 * Whole axis-aligned rect inside the region. Corner containment alone is NOT enough on a concave
 * region — a notch narrower than the rect can pass between two corners — so this also rejects any
 * ring vertex strictly inside the rect and any ring edge properly crossing a rect edge.
 */
export function rectInRegion(x: number, y: number, w: number, h: number, ring: readonly Pt[]): boolean {
  const cs = rectCorners(x, y, w, h);
  for (const c of cs) if (!pointInPolygon(c, ring)) return false;
  for (const v of ring) if (strictlyInRect(v, x, y, w, h)) return false;
  for (let i = 0, n = ring.length; i < n; i += 1) {
    const a = ring[i];
    const b = ring[(i + 1) % n];
    for (let k = 0; k < 4; k += 1) {
      if (segmentsProperlyCross(a, b, cs[k], cs[(k + 1) % 4])) return false;
    }
  }
  return true;
}

/**
 * Any genuine overlap between an axis-aligned rect and a region. Boundary contact does not count:
 * a panel flush against a keep-out edge is legal, a panel inside it is not.
 */
export function rectIntersectsRegion(
  x: number,
  y: number,
  w: number,
  h: number,
  ring: readonly Pt[],
): boolean {
  const cs = rectCorners(x, y, w, h);
  for (const c of cs) if (pointInPolygon(c, ring)) return true;
  for (const v of ring) if (strictlyInRect(v, x, y, w, h)) return true;
  for (let i = 0, n = ring.length; i < n; i += 1) {
    const a = ring[i];
    const b = ring[(i + 1) % n];
    for (let k = 0; k < 4; k += 1) {
      if (segmentsProperlyCross(a, b, cs[k], cs[(k + 1) % 4])) return true;
    }
  }
  return false;
}
